// A clip's animations at a moment (#266, spec docs/specs/editor/animations.md): each one's
// progress through its cycle, eased, read off its preset's stops, and all of a clip's folded
// into one motion the scene draws (`scene.ts`). Pure: the preview, Download and the Save worker
// get the same motion for the same clip and time.
import type { AnimationEasing } from "../../../api/generated/model";
import {
  AT_REST,
  POSE_AT_REST,
  PRESETS,
  STAGGERS,
  startOf,
  type AnimatedProp,
  type Animation,
  type ElementPose,
  type MaskShape,
  type StaggerInfo,
  type Stop,
} from "../animations";

/** Back's overshoot, the usual constant. */
const BACK = 1.70158;

/** Live's easings, by name: `t` 0 to 1 in, eased out (Back passes the ends). */
export function eased(easing: AnimationEasing, t: number): number {
  switch (easing) {
    case "linear":
      return t;
    case "slow": {
      // Slow motion: steady through the middle half (0.25 + 0.5 t), easing into and out of it.
      const r = 0.25 + 0.5 * t;
      if (t < 0.25) {
        const p = 1 - t / 0.25;
        return r - p * p * p * p * r;
      }
      if (t > 0.75) {
        const p = (t - 0.75) / 0.25;
        return r + (t - r) * p * p * p * p;
      }
      return r;
    }
    case "ease-in-quad":
      return t * t;
    case "ease-out-quad":
      return t * (2 - t);
    case "ease-in-out-quad":
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    case "ease-in-cubic":
      return t * t * t;
    case "ease-out-cubic":
      return (t - 1) ** 3 + 1;
    case "ease-in-out-cubic":
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    case "ease-in-back":
      return t * t * ((BACK + 1) * t - BACK);
    case "ease-out-back": {
      const u = t - 1;
      return u * u * ((BACK + 1) * u + BACK) + 1;
    }
  }
}

/**
 * The value of `prop` at `percent` of a cycle, read off the stops: straight between the two
 * stops around it, a stop that leaves it unset counting as its rest (live's rule), and past the
 * ends (a Back easing) carried on along the end segment.
 */
export function valueAt(stops: readonly Stop[], prop: AnimatedProp, percent: number): number {
  const at = (stop: Stop) => stop.values[prop] ?? AT_REST[prop];
  if (stops.length === 0) return AT_REST[prop];
  if (stops.length === 1) return at(stops[0]!);
  let index = 0;
  while (index < stops.length - 2 && percent > stops[index + 1]!.at) index += 1;
  const from = stops[index]!;
  const to = stops[index + 1]!;
  const span = to.at - from.at;
  if (span <= 0) return at(to);
  return at(from) + ((at(to) - at(from)) * (percent - from.at)) / span;
}

/** Where a mask preset stands: its shape and settings, and how much of the clip shows, 0 to 1. */
export interface MaskAt {
  shape: MaskShape;
  animation: Animation;
  progress: number;
}

/** Where a Block Reveal stands: its settings and its eased progress, 0 to 1. */
export interface BlockAt {
  animation: Animation;
  progress: number;
}

/** Where a by-word or by-character preset stands: its settings and its cycle, 0 to 1, uneased
 * (each word or letter eases its own part). */
export interface StaggerAt {
  animation: Animation;
  info: StaggerInfo;
  progress: number;
}

/** A clip's motion: offsets add, multipliers multiply, a mask preset's mask is the latest one's. */
export interface Motion {
  /** Live's pixels and degrees, in the clip's own turned frame. */
  x: number;
  y: number;
  angle: number;
  scaleX: number;
  scaleY: number;
  opacity: number;
  /** Live's pixels of blur; motion blur as live's zoom blur's strength × 200. */
  blur: number;
  motionBlur: number;
  brightness: number;
  saturation: number;
  /** Live's mirrored tiling around a video or a still. */
  mirror: boolean;
  mask: MaskAt | null;
  /** A text's Block Reveal (#266 part 2), the latest started. */
  block: BlockAt | null;
  /** A text's by-word or by-character preset (#266 part 2b), the latest started. */
  stagger: StaggerAt | null;
  /** Custom's (part 3): the drawn size's change and a centred mask box's, in live's pixels; a
   * text's letters' size change (live's pixels), tracking (of the size) and line pitch; a
   * picture's crop of its file (its pixels). */
  width: number;
  height: number;
  maskBox: { width: number; height: number; radius: number } | null;
  fontSize: number;
  tracking: number;
  lineHeight: number;
  crop: { x: number; y: number; width: number; height: number };
}

export const STILL: Motion = {
  x: 0,
  y: 0,
  angle: 0,
  scaleX: 1,
  scaleY: 1,
  opacity: 1,
  blur: 0,
  motionBlur: 0,
  brightness: 1,
  saturation: 1,
  mirror: false,
  mask: null,
  block: null,
  stagger: null,
  width: 0,
  height: 0,
  maskBox: null,
  fontSize: 0,
  tracking: 0,
  lineHeight: 1,
  crop: { x: 0, y: 0, width: 0, height: 0 },
};

/** How far through its cycle an animation is at `offsetMs` from its start, eased; null before. */
export function cycleAt(animation: Animation, offsetMs: number): number | null {
  if (offsetMs < 0) return null;
  // Done: its last stop, held to the clip's end.
  if (offsetMs >= animation.durationMs) return 1;
  const cycle = animation.durationMs / animation.iterations;
  return eased(animation.easing, (offsetMs % cycle) / cycle);
}

/**
 * The clip's motion at `atMs` of its own `clipMs`: every animation started by then (an In or a
 * combo from its delay, an Out so as to end at the clip's end), in the order they start, its
 * values read off its stops at its eased progress.
 */
export function motionAt(animations: readonly Animation[], atMs: number, clipMs: number): Motion {
  if (animations.length === 0) return STILL;
  const ordered = [...animations].sort((one, other) => startOf(one, clipMs) - startOf(other, clipMs));
  const motion = { ...STILL, crop: { ...STILL.crop } };
  for (const animation of ordered) {
    const offset = atMs - startOf(animation, clipMs);
    const staggers = STAGGERS[animation.preset];
    if (staggers !== undefined) {
      // An In shows its words at their start before it runs (live's); an Out leaves them be.
      if (offset < 0 && animation.mode === "out") continue;
      const cycle = animation.durationMs / animation.iterations;
      const raw = offset < 0 ? 0 : offset >= animation.durationMs ? 1 : (offset % cycle) / cycle;
      motion.stagger = { animation, info: staggers, progress: raw };
      continue;
    }
    const progress = cycleAt(animation, offset);
    if (progress === null) continue;
    const info = PRESETS[animation.preset];
    if (animation.preset === "block-reveal") {
      motion.block = { animation, progress };
      continue;
    }
    const stops = info.stops(animation, animation.mode);
    const percent = progress * 100;
    const value = (prop: AnimatedProp) => valueAt(stops, prop, percent);
    motion.x += value("x");
    motion.y += value("y");
    motion.angle += value("angle");
    const scale = value("scale");
    motion.scaleX *= scale * value("scaleX");
    motion.scaleY *= scale * value("scaleY");
    motion.opacity *= Math.max(0, value("opacity"));
    motion.blur += Math.max(0, value("blur"));
    motion.motionBlur += Math.max(0, value("motionBlur"));
    motion.brightness *= Math.max(0, value("brightness"));
    motion.saturation *= Math.max(0, value("saturation"));
    if (value("mirror") > 0.5) motion.mirror = true;
    if (info.mask !== null) {
      motion.mask = { shape: info.mask, animation, progress: Math.min(1, Math.max(0, value("maskProgress"))) };
    }
    if (animation.preset === "custom") {
      // Custom's mask: a box grown from the middle by its Mask Progress, its corners rounded.
      const reveal = value("maskProgress");
      if (reveal < 1) {
        motion.mask = {
          shape: "center",
          animation: { ...animation, axis: "both", initial: 0, borderRadius: Math.max(0, value("maskBorderRadius")) },
          progress: Math.min(1, Math.max(0, reveal)),
        };
      }
      const [maskWidth, maskHeight, maskRadius] = [value("maskWidth"), value("maskHeight"), value("maskRadius")];
      if (maskWidth !== 0 || maskHeight !== 0 || maskRadius !== 0) {
        motion.maskBox = { width: maskWidth, height: maskHeight, radius: Math.max(0, maskRadius) };
      }
      motion.width += value("width");
      motion.height += value("height");
      motion.fontSize += value("fontSize");
      motion.tracking += value("tracking");
      motion.lineHeight *= Math.max(0.1, value("lineHeight"));
      motion.crop.x += value("cropX");
      motion.crop.y += value("cropY");
      motion.crop.width += value("cropWidth");
      motion.crop.height += value("cropHeight");
    }
  }
  motion.opacity = Math.min(1, motion.opacity);
  return motion;
}

/** A number from 0 to 1 for a seed, the same every time (mulberry32). */
function seeded(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** Char Scramble's order: the letters shuffled (the same for the same animation), three at a time. */
function scrambledSlots(id: string, count: number): number[] {
  let seed = 0;
  for (const char of id) seed = (Math.imul(seed, 31) + char.charCodeAt(0)) >>> 0;
  const next = seeded(seed);
  const order = Array.from({ length: count }, (_, at) => at);
  for (let at = count - 1; at > 0; at -= 1) {
    const pick = Math.floor(next() * (at + 1));
    [order[at], order[pick]] = [order[pick]!, order[at]!];
  }
  const slots = Array.from({ length: count }, () => 0);
  order.forEach((element, place) => {
    slots[element] = Math.floor(place / 3);
  });
  return slots;
}

/**
 * Word (or letter) `index` of `count` at the stagger's progress, as live's stagger clock runs:
 * each starts `Stagger` seconds after the one before, takes its own time (the preset's, else the
 * animation's duration), and the whole run is fitted into the animation's duration. An In goes
 * from the preset's pose to rest, an Out from rest to it.
 */
export function poseAt(stagger: StaggerAt, index: number, count: number): ElementPose {
  const { animation, info, progress } = stagger;
  const each = animation.stagger ?? info.each;
  const tween = info.tweenS ?? animation.durationMs / 1000;
  const slots = info.scramble ? scrambledSlots(animation.id, count) : null;
  const slot = slots?.[index] ?? index;
  const last = slots === null ? count - 1 : Math.max(0, ...slots);
  // Live's yoyo plays each piece's time twice, there and back: its In ends where it began.
  const yoyo = info.yoyo === true && animation.mode !== "out";
  const span = yoyo ? 2 * tween : tween;
  const total = last * each + span;
  const at = progress * total - slot * each;
  const run = span <= 0.001 ? (at >= 0 && progress > 0 ? 1 : 0) : Math.min(1, Math.max(0, at / span));
  const local = yoyo ? 1 - Math.abs(1 - 2 * run) : run;
  const e = eased(animation.easing, local);
  // How far from rest towards the preset's pose: an In leaves it, an Out goes to it.
  const away = animation.mode === "out" ? e : 1 - e;
  const from = { ...POSE_AT_REST, ...info.from(animation) };
  const between = (rest: number, pose: number) => rest + (pose - rest) * away;
  const pose: ElementPose = {
    alpha: Math.min(1, Math.max(0, between(1, from.alpha))),
    x: between(0, from.x),
    y: between(0, from.y),
    scaleX: between(1, from.scaleX),
    scaleY: between(1, from.scaleY),
    rotation: between(0, from.rotation),
    skewX: between(0, from.skewX),
    blur: Math.max(0, between(0, from.blur)),
  };
  if (info.scaleFrames !== undefined) {
    // Pop By Word: through its stops; its Out the same backwards.
    const stops = info.scaleFrames.map(([percent, scale]) => ({ at: percent, values: { scale } }));
    const scale = valueAt(stops, "scale", (animation.mode === "out" ? 1 - e : e) * 100);
    pose.scaleX = scale;
    pose.scaleY = scale;
  }
  return pose;
}
